import { useEffect, useMemo } from 'react';

import { useColorMode, useClipboard } from '@vimeo/bokeh';
import { Callable } from '@vimeo/core/types/generic';
import {
  GetInteractionRoomRoomIdEmbedCodeOptions,
  GetInteractionRoomRoomIdEmbedCodeFields,
} from '@vimeo/gctl-api/fetchers/interaction_room/room_id/embed_code';
import { useGetInteractionRoomRoomIdEmbedCodeLazy } from '@vimeo/gctl-api/hooks/useInteractionRoomRoomIdEmbedCode';

import { useLiveGlobals } from '../../components/providers/globals';
import { IInteractionStoreInitialState } from '../../types/store';

type TInteractionToolsEmbedParams =
  GetInteractionRoomRoomIdEmbedCodeOptions<GetInteractionRoomRoomIdEmbedCodeFields>['query'];
type TInteractionToolsRoomType =
  GetInteractionRoomRoomIdEmbedCodeOptions<GetInteractionRoomRoomIdEmbedCodeFields>['where']['roomType'];

export function useInteractionToolsPageLink(): {
  isLoading: boolean;
  value: string;
  hasCopied: boolean;
  onCopy: Callable;
} {
  const {
    initialState: { sessionType, sessionId },
  } = useLiveGlobals<IInteractionStoreInitialState>();
  const { colorMode } = useColorMode();
  const { onCopy, value, setValue, hasCopied } = useClipboard('');

  const [interactionToolsUrlFetcher, { called, loading, data, error }] =
    useGetInteractionRoomRoomIdEmbedCodeLazy();

  useEffect(() => {
    if (sessionType && sessionId && interactionToolsUrlFetcher) {
      interactionToolsUrlFetcher({
        select: ['url'],
        where: { roomType: sessionType as TInteractionToolsRoomType, roomId: String(sessionId) },
        query: { module: 'auto', theme: colorMode } as TInteractionToolsEmbedParams,
      });
    }
  }, [colorMode, interactionToolsUrlFetcher, sessionId, sessionType]);

  useEffect(() => {
    if (called && !loading && !error && data) {
      setValue(data?.url);
    }
  }, [called, data, error, loading, setValue]);

  return useMemo(
    () => ({
      isLoading: loading,
      value,
      onCopy,
      hasCopied,
    }),
    [loading, value, hasCopied, onCopy],
  );
}
